import { supabase } from './supabase'
import { generateBillingPeriods, periodAmount } from './billingPeriods'
import type { BillingMilestone } from '../types/agreement'
import { isUsageBasedUnitType } from './unitTypeHelpers'
import { matchesDivision, type DivisionSelection } from './divisionFiltering'

export type ReadyToBillKind =
  | 'contract_period'
  | 'completed_job'
  | 'milestone'
  | 'rate_card'
  | 'per_unit'
  | 'hourly'

export interface ReadyToBillItem {
  key: string
  kind: ReadyToBillKind
  customer_id: string
  customer_name: string
  property_id?: string | null
  contract_id?: string | null
  job_id?: string | null
  milestone_id?: string | null
  label: string
  amount: number
  period_start?: string
  period_end?: string
  billing_frequency?: string
  operational_division_id?: string | null
  /** True when this item cannot be safely auto-invoiced — see blocked_reason. */
  blocked?: boolean
  blocked_reason?: string
  scenario?: string
}

/** Job ids that already have an invoice line item — the "already invoiced" guard. */
export async function fetchAlreadyInvoicedJobIds(): Promise<Set<string>> {
  const { data: invoicedJobRows } = await supabase
    .from('invoice_items')
    .select('job_id')
    .not('job_id', 'is', null)
  return new Set((invoicedJobRows || []).map((r) => r.job_id))
}

export async function fetchReadyToBillItems(
  division?: DivisionSelection | null
): Promise<ReadyToBillItem[]> {
  const items: ReadyToBillItem[] = []

  const { data: customers } = await supabase
    .from('customers')
    .select('id, company_name, contact_first_name, contact_last_name')

  const customerName = (id: string) => {
    const c = (customers || []).find((x) => x.id === id)
    if (!c) return 'Unknown'
    return (
      c.company_name ||
      `${c.contact_first_name || ''} ${c.contact_last_name || ''}`.trim() ||
      'Unknown'
    )
  }

  // Already invoiced job ids
  const invoicedJobIds = await fetchAlreadyInvoicedJobIds()

  // Already invoiced contract periods
  const { data: periodInvoices } = await supabase
    .from('invoices')
    .select('contract_id, period_start, period_end')
    .not('period_start', 'is', null)
  const invoicedPeriods = new Set(
    (periodInvoices || []).map((i) => `${i.contract_id}:${i.period_start}:${i.period_end}`)
  )

  // 1. Contract periods due
  const { data: contracts } = await supabase
    .from('contracts')
    .select(
      'id, customer_id, property_id, title, contract_number, start_date, end_date, billing_frequency, billing_method, billing_day_of_month, total_amount, contract_total_amount, status, operational_division_id, service_type'
    )
    // One-time contracts use 'signed'/'in_process' as their active-equivalent statuses
    .in('status', ['active', 'signed', 'in_process'])
    .in('billing_method', ['per_period', 'divide_total'])

  for (const c of (contracts || []).filter((contract) => matchesDivision(contract, division))) {
    const freq = c.billing_frequency
    if (!freq || freq === 'one_time' || freq === 'progress' || freq === 'after_each_service') continue
    const billDay = Number((c as any).billing_day_of_month) || 1
    const periods = generateBillingPeriods(c.start_date, c.end_date, freq, billDay)
    const perPeriod = Number(c.total_amount || 0)
    const total = Number(c.contract_total_amount || c.total_amount || 0)
    for (const p of periods) {
      const pk = `${c.id}:${p.start}:${p.end}`
      if (invoicedPeriods.has(pk)) continue
      const amt = periodAmount(c.billing_method, total, perPeriod, periods.length)
      if (amt <= 0) continue
      items.push({
        key: `period-${pk}`,
        kind: 'contract_period',
        customer_id: c.customer_id,
        customer_name: customerName(c.customer_id),
        property_id: c.property_id,
        contract_id: c.id,
        operational_division_id: c.operational_division_id || null,
        label: `${c.contract_number || c.title} — ${p.label}`,
        amount: amt,
        period_start: p.start,
        period_end: p.end,
        billing_frequency: freq,
        scenario: billDay === 1 ? 'Monthly period (bill day 1)' : `Period (bill day ${billDay >= 31 ? 'last' : billDay})`,
      })
    }
  }

  // 2. Approved-but-uninvoiced jobs (the billable gate is job review approval,
  //    not merely "completed" — a job must clear QC/review before it can bill).
  const { data: jobs } = await supabase
    .from('jobs')
    .select(
      'id, job_number, title, customer_id, property_id, contract_id, billing_type, billing_contract_id, quoted_amount, actual_amount, total_billable_amount, materials_cost, status, scheduled_date, operational_division_id, service_type'
    )
    .eq('status', 'approved')

  const divisionJobs = (jobs || []).filter((job) => matchesDivision(job, division))
  const contractIds = Array.from(
    new Set(divisionJobs.map((j) => j.billing_contract_id || j.contract_id).filter(Boolean))
  )
  const { data: contractRows } = contractIds.length
    ? await supabase
        .from('contracts')
        .select('id, billing_method, billing_frequency')
        .in('id', contractIds)
    : { data: [] }
  const contractMap = new Map((contractRows || []).map((c) => [c.id, c]))

  // Time & materials jobs: bill approved time_entries × employee hourly_rate + materials.
  const tmJobIds = divisionJobs
    .filter((j) => (j.billing_type || 'direct') === 'time_materials' && !invoicedJobIds.has(j.id))
    .map((j) => j.id)
  const { data: tmEntries } = tmJobIds.length
    ? await supabase
        .from('time_entries')
        .select('job_id, employee_id, total_hours, is_approved')
        .in('job_id', tmJobIds)
    : { data: [] }
  const tmEmployeeIds = Array.from(
    new Set((tmEntries || []).map((e) => e.employee_id).filter(Boolean))
  )
  const { data: tmEmployees } = tmEmployeeIds.length
    ? await supabase.from('employees').select('id, hourly_rate').in('id', tmEmployeeIds)
    : { data: [] }
  const employeeRateMap = new Map((tmEmployees || []).map((e) => [e.id, Number(e.hourly_rate || 0)]))

  // Line items for direct / rate-card / per-unit resolution
  const directJobIds = divisionJobs
    .filter((j) => {
      const bt = j.billing_type || 'direct'
      return bt === 'direct' && !invoicedJobIds.has(j.id)
    })
    .map((j) => j.id)
  const { data: lineRows } = directJobIds.length
    ? await supabase
        .from('job_line_items')
        .select('job_id, description, quantity, actual_quantity, unit_type, unit_price, total_price, is_variable, is_billable')
        .in('job_id', directJobIds)
    : { data: [] }
  const linesByJob = new Map<string, typeof lineRows>()
  for (const row of lineRows || []) {
    const list = linesByJob.get(row.job_id) || []
    list.push(row)
    linesByJob.set(row.job_id, list)
  }

  for (const j of divisionJobs) {
    if (invoicedJobIds.has(j.id)) continue
    const bt = j.billing_type || 'direct'
    if (bt === 'included') continue

    const pushJob = (
      amount: number,
      opts?: { blocked?: boolean; blocked_reason?: string; kind?: ReadyToBillKind; scenario?: string }
    ) => {
      items.push({
        key: `job-${j.id}`,
        kind: opts?.kind || 'completed_job',
        customer_id: j.customer_id,
        customer_name: customerName(j.customer_id),
        property_id: j.property_id,
        contract_id: j.billing_contract_id || j.contract_id,
        job_id: j.id,
        operational_division_id: j.operational_division_id || null,
        label: `${j.job_number} — ${j.title}`,
        amount,
        blocked: opts?.blocked,
        blocked_reason: opts?.blocked_reason,
        scenario: opts?.scenario,
      })
    }

    if (bt === 'contract') {
      const cid = j.billing_contract_id || j.contract_id
      const contract = cid ? contractMap.get(cid) : null
      if (!contract) {
        pushJob(0, {
          blocked: true,
          blocked_reason: 'Billing type is "contract" but the linked contract could not be found.',
          scenario: 'Contract',
        })
        continue
      }
      if (contract.billing_method !== 'actual_usage') continue
      if (contract.billing_frequency && contract.billing_frequency !== 'after_each_service') {
        pushJob(Number(j.total_billable_amount || j.actual_amount || j.quoted_amount || 0), {
          blocked: true,
          blocked_reason: `Contract bills actual usage on a ${contract.billing_frequency} cycle, which isn't aggregated automatically — bill this job manually.`,
          scenario: 'Contract usage',
        })
        continue
      }
      const amt = Number(j.total_billable_amount || j.actual_amount || j.quoted_amount || 0)
      if (amt <= 0) continue
      pushJob(amt, { scenario: 'Contract (after each service)' })
      continue
    }

    if (bt === 'time_materials') {
      const entries = (tmEntries || []).filter((e) => e.job_id === j.id)
      const hasUnapproved = entries.some((e) => !e.is_approved)
      const approvedCost = entries
        .filter((e) => e.is_approved)
        .reduce((s, e) => s + Number(e.total_hours || 0) * (employeeRateMap.get(e.employee_id) || 0), 0)
      const materials = Number(j.materials_cost || 0)
      const amt = approvedCost + materials
      if (hasUnapproved) {
        pushJob(amt, {
          blocked: true,
          blocked_reason: 'Unapproved time entries on this job must be approved before billing.',
          kind: 'hourly',
          scenario: 'Hourly / T&M',
        })
        continue
      }
      if (entries.length === 0 && amt <= 0) {
        pushJob(0, {
          blocked: true,
          blocked_reason: 'No approved time entries and no materials cost — nothing to bill yet.',
          kind: 'hourly',
          scenario: 'Hourly / T&M',
        })
        continue
      }
      if (amt <= 0) continue
      pushJob(amt, { kind: 'hourly', scenario: 'Hourly / T&M' })
      continue
    }

    // Direct: distinguish flat vs rate-card / per-unit from job line items
    const lines = (linesByJob.get(j.id) || []).filter((l) => l.is_billable !== false)
    const usageLines = lines.filter((l) => isUsageBasedUnitType(l.unit_type))
    const perUnitLines = usageLines.filter((l) =>
      ['per_unit', 'per_apartment', 'per_bedroom', 'per_bathroom', 'per_sqft'].includes(
        String(l.unit_type || '').toLowerCase()
      )
    )
    const rateCardLike = usageLines.length > 0

    if (rateCardLike) {
      const missingQty = usageLines.some((l) => {
        const qty = l.actual_quantity != null ? Number(l.actual_quantity) : Number(l.quantity || 0)
        return !Number.isFinite(qty) || qty <= 0
      })
      const lineTotal = usageLines.reduce((s, l) => {
        const qty = l.actual_quantity != null ? Number(l.actual_quantity) : Number(l.quantity || 0)
        return s + qty * Number(l.unit_price || 0)
      }, 0)
      const flatExtra = lines
        .filter((l) => !isUsageBasedUnitType(l.unit_type))
        .reduce((s, l) => s + Number(l.total_price || 0), 0)
      const amt = lineTotal + flatExtra

      if (missingQty) {
        pushJob(amt, {
          blocked: true,
          blocked_reason:
            perUnitLines.length > 0
              ? 'Per-unit / usage quantities are missing or zero — confirm counts before billing.'
              : 'Rate-card line quantities are missing or zero — confirm usage before billing.',
          kind: perUnitLines.length > 0 ? 'per_unit' : 'rate_card',
          scenario: perUnitLines.length > 0 ? 'Per unit' : 'Price list / rate card',
        })
        continue
      }
      if (amt <= 0) {
        pushJob(0, {
          blocked: true,
          blocked_reason: 'Rate-card / per-unit lines total $0 — check unit prices and quantities.',
          kind: perUnitLines.length > 0 ? 'per_unit' : 'rate_card',
          scenario: perUnitLines.length > 0 ? 'Per unit' : 'Price list / rate card',
        })
        continue
      }
      pushJob(amt, {
        kind: perUnitLines.length > 0 ? 'per_unit' : 'rate_card',
        scenario: perUnitLines.length > 0 ? 'Per unit' : 'Price list / rate card',
      })
      continue
    }

    // Flat direct
    const fromLines = lines.reduce((s, l) => s + Number(l.total_price || 0), 0)
    const amt = Number(j.total_billable_amount || j.actual_amount || fromLines || j.quoted_amount || 0)
    if (amt <= 0) {
      pushJob(0, {
        blocked: true,
        blocked_reason: 'No billable amount on this job (quoted/actual/line totals are empty).',
        scenario: 'Flat / direct',
      })
      continue
    }
    pushJob(amt, { scenario: 'Flat / direct' })
  }

  // 3. Pending milestones
  const { data: milestones } = await supabase
    .from('agreement_billing_milestones')
    .select('id, label, amount, percentage, contract_id, proposal_id, status')
    .eq('status', 'pending')

  const milestoneContractIds = Array.from(
    new Set((milestones || []).map((m) => m.contract_id).filter(Boolean))
  )
  const { data: milestoneContracts } = milestoneContractIds.length
    ? await supabase
        .from('contracts')
        .select('id, customer_id, property_id, title, contract_number, operational_division_id, service_type')
        .in('id', milestoneContractIds)
    : { data: [] }
  const mcMap = new Map((milestoneContracts || []).map((c) => [c.id, c]))

  for (const m of milestones || []) {
    if (!m.contract_id) continue
    const c = mcMap.get(m.contract_id)
    if (!c || !matchesDivision(c, division)) continue
    items.push({
      key: `milestone-${m.id}`,
      kind: 'milestone',
      customer_id: c.customer_id,
      customer_name: customerName(c.customer_id),
      property_id: c.property_id,
      contract_id: m.contract_id,
      milestone_id: m.id,
      operational_division_id: c.operational_division_id || null,
      label: `${c.contract_number || c.title} — ${m.label} (${m.percentage}%)`,
      amount: Number(m.amount || 0),
    })
  }

  return items.sort((a, b) => a.customer_name.localeCompare(b.customer_name))
}

export async function fetchMilestoneForInvoice(milestoneId: string): Promise<{
  milestone: BillingMilestone
  contract: {
    id: string
    customer_id: string
    property_id?: string | null
    payment_terms?: number
    title?: string
    operational_division_id?: string | null
  }
} | null> {
  const { data: m } = await supabase
    .from('agreement_billing_milestones')
    .select('*')
    .eq('id', milestoneId)
    .single()
  if (!m?.contract_id) return null
  const { data: c } = await supabase
    .from('contracts')
    .select('id, customer_id, property_id, payment_terms, title, operational_division_id')
    .eq('id', m.contract_id)
    .single()
  if (!c) return null
  return { milestone: m as BillingMilestone, contract: c }
}
